import React from "react";
import Table from "views/merchandise/tables/BulkActionsTable";
import { AMC_REPORT_METRIC_OPTIONS, TABLE_SPECS_BY_REPORT_TYPE } from "./utils";
import { useMemo, useRef } from "react";
import useCustomTableFilters from "hooks/useCustomTableFilters";
import { useAppSelector } from "redux/hooks";
import NoImage from "assets/images/utility/no_image.png";
import { snakeCaseToSentenceCase, toTitleCase } from "utils/formatText";
import getURLPrefix from "utils/getUrlPrefix";
import { FaArrowRight } from "react-icons/fa";
import AsinProductCell from "views/merchandise/cells/AsinCell";

const URL_PREFIX = getURLPrefix();

const AMCReportTable = ({ reportData, reportDetails, queryDetails }) => {
  const tableRef = useRef();
  const marketPlace = useAppSelector((state) => state.marketPlace);

  const TABLE_ID = `dashboardReport-${queryDetails?.name ?? ""}`;
  const filterProps = useCustomTableFilters(TABLE_ID);

  const [columnSpecs, defaultSorted, titleColLabel, searchFilterTargets] = useMemo(() => {
    const reportTypeSpec = TABLE_SPECS_BY_REPORT_TYPE[reportDetails?.query_type?.split("_INTERNAL")?.[0]];
    const reportTypeColumns = reportTypeSpec?.metrics?.map((metricId) => {
      return AMC_REPORT_METRIC_OPTIONS.find((m) => m.key === metricId);
    });
    const reportTypeDefaultSorted = reportTypeSpec?.defaultSorted;
    const titleColLabel = reportTypeSpec?.titleColLabel;
    const searchFilterTargets = reportTypeSpec?.searchFilterTargets;

    return [reportTypeColumns, reportTypeDefaultSorted, titleColLabel, searchFilterTargets];
  }, [reportDetails]);

  const filteredReportData = useMemo(() => {
    const filteredData = reportData;

    // For some reports, we're getting rows that are blank, filter them out and ask Mike to look if it's an easy fix in the AMC queries
    if (["CAMPAIGN_STR_PLACEMENT_PERFORMANCE"].includes(queryDetails.name)) {
      return filteredData?.filter((row) => row.customer_search_term);
    }

    if (["EXPOSURE_SEGMENT_PERFORMANCE"].includes(queryDetails.name)) {
      return filteredData?.filter((row) => row.exposure_group);
    }

    if (["DSP_CAMPAIGN_PERFORMANCE_BY_LOCATION"].includes(queryDetails.name)) {
      return filteredData?.filter((row) => row.campaign);
    }

    if (["CAMPAIGN_PERFORANCE_FIRST_TOUCH_ATTRIBUTION"].includes(queryDetails.name)) {
      return filteredData?.filter((row) => row.attribution_model);
    }

    if (["LTV_BY_AD_EXPOSURE_INTERNAL"].includes(queryDetails.name)) {
      return filteredData?.filter((row) => row.exposure);
    }

    if (["REACH_IMPRESSION_FREQUENCY"].includes(queryDetails.name)) {
      return filteredData?.filter((row) => row.frequency_bucket);
    }

    if (["ASIN_PURCHASE_OVERLAP"].includes(queryDetails.name)) {
      return filteredData?.filter((row) => row.lead_asin);
    }

    if (["TIME_TO_CONVERSION"].includes(queryDetails.name)) {
      return filteredData?.filter((row) => row.campaign);
    }
    if (["NEW_TO_BRAND_CONVERSIONS"].includes(queryDetails.name)) {
      return filteredData?.filter((row) => row.campaign);
    }
    return filteredData;
  }, [queryDetails, reportData]);

  const renderTitleColumn = (row, queryType) => {
    switch (queryType) {
      case "GATEWAY_ASINS":
      case "GATEWAY_ASINS_INTERNAL":
      case "SUBSCRIBE_AND_SAVE_PERFORMANCE":
      case "SUBSCRIBE_AND_SAVE_PERFORMANCE_INTERNAL": {
        const { tracked_asin } = row.original ?? {};
        const productImageUrl = `${URL_PREFIX}/api/productimage/?asin=${tracked_asin}`;

        return (
          <div className="d-flex" style={{ fontWeight: 500 }}>
            {" "}
            <div
              style={{
                userSelect: "none",
                WebkitUserSelect: "none",
                flexShrink: 0,
                width: 50,
                height: 50,
                marginRight: "10px",
                background: `url(${productImageUrl}) no-repeat center / contain, url("${NoImage}") no-repeat center / contain`,
              }}
              draggable="false"
            />
            <span>{tracked_asin}</span>
          </div>
        );
      }
      case "LTV_BY_AD_EXPOSURE":
      case "LTV_BY_AD_EXPOSURE_INTERNAL": {
        const { exposure } = row.original ?? {};
        const isTotalRow = exposure === "total";

        return (
          <span style={{ fontWeight: isTotalRow ? 700 : 500, textDecoration: isTotalRow ? "underline" : "inherit" }}>
            {toTitleCase(exposure)}
          </span>
        );
      }
      case "EXPOSURE_SEGMENT_PERFORMANCE":
      case "EXPOSURE_SEGMENT_PERFORMANCE_INTERNAL": {
        const { exposure_group } = row.original ?? {};
        const touchpoints = exposure_group?.slice(1, -1).split(",");
        return (
          <span style={{ fontWeight: 500 }}>
            {touchpoints.map((touchpoint, index) => {
              return (
                <React.Fragment key={index}>
                  <span>{toTitleCase(snakeCaseToSentenceCase(touchpoint))}</span>
                  {index !== touchpoints?.length - 1 && <FaArrowRight className="mx-2" />}
                </React.Fragment>
              );
            })}
          </span>
        );
      }
      case "CAMPAIGN_STR_PLACEMENT_PERFORMANCE": {
        const { customer_search_term } = row.original ?? {};
        return <span style={{ fontWeight: 500 }}>{customer_search_term}</span>;
      }
      case "DSP_CAMPAIGN_PERFORMANCE_BY_LOCATION":
      case "CAMPAIGN_PERFORANCE_FIRST_TOUCH_ATTRIBUTION":
      case "CAMPAIGN_PERFORANCE_FIRST_TOUCH_ATTRIBUTION_INTERNAL": {
        const { campaign } = row.original ?? {};

        return <span style={{ fontWeight: 500 }}>{campaign}</span>;
      }
      case "REACH_IMPRESSION_FREQUENCY": {
        const { frequency_bucket } = row.original ?? {};
        return <span>{frequency_bucket}</span>;
      }
      case "ASIN_PURCHASE_OVERLAP": {
        const { lead_asin } = row.original ?? {};

        return <AsinProductCell asin={lead_asin} />;
      }

      case "TIME_TO_CONVERSION": {
        const { campaign } = row.original ?? {};
        return <span>{campaign}</span>;
      }
      case "NEW_TO_BRAND_CONVERSIONS": {
        const { campaign } = row.original ?? {};

        return <span>{campaign}</span>;
      }

      default:
        return <span>Placeholder</span>;
    }
  };
  return (
    <Table
      marketPlace={marketPlace}
      tableRef={tableRef}
      tableId={TABLE_ID}
      isExpandable={false}
      isSelectable={false}
      titleCol={{
        id: "title",
        isStatic: true,
        Header: titleColLabel,
        accessor: "text",
        format: "text",
        style: { width: "300px" },
        width: 300,
        Cell: (row) => {
          return renderTitleColumn(row, reportDetails?.query_type);
        },
        Filter: () => null,
      }}
      data={filteredReportData}
      columnSpecs={columnSpecs}
      getSearchCriteria={(row) => {
        let searchResultsString = "";

        searchFilterTargets?.forEach((targetProperty) => {
          searchResultsString = `${searchResultsString} ${row[targetProperty]}`;
        });
        return searchResultsString;
      }}
      tableRowDescription={"Products"}
      defaultSorted={defaultSorted}
      isLoading={false}
      filterWidget={true}
      hideFilterToggleIcon={true}
      hideResetFiltersIcon={false}
      {...filterProps}
      handleBulkChanges={() => {}}
      minRows={1}
    />
  );
};

export default AMCReportTable;
